import React from 'react';
import { Icon } from '@saviynt/design-system';

import type { AttributeRowProps } from '../../data-layer/types';
import AttributeDropdown from '../AttributeDropdown/AttributeDropdown';

import TextFieldsSVG from './TextFieldsSVG';
import TrashIconSVG from './TrashIconSVG';

import './AttributeRow.css';

/**
 * AttributeRow component for individual mapping rows
 */
function AttributeRow({
  row,
  availableSaviyntFields,
  onUpdate,
  onDelete,
  disabled = false,
  canDelete = true,
  validationError,
  onBlur,
}: AttributeRowProps) {
  const handleSaviyntFieldChange = (value: string) => {
    onUpdate(row.id, 'saviyntField', value);
  };

  const handleExternalFieldChange = (value: string) => {
    onUpdate(row.id, 'externalAppField', value);
  };

  const handleDelete = () => {
    onDelete(row.id);
  };

  return (
    <div className='attribute-mapping-row'>
      {/* Left Side - Saviynt Field (Dropdown) */}
      <div className='saviynt-field'>
        <div className='saviynt-field-inner'>
          <div className='field-icon'>
            <TextFieldsSVG />
          </div>
          <div className='field-input'>
            <AttributeDropdown
              options={availableSaviyntFields}
              value={row.saviyntField}
              onChange={handleSaviyntFieldChange}
              placeholder='Select Option'
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* Arrow */}
      <div className='mapping-arrow'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='80'
          height='72'
          viewBox='0 0 80 72'>
          <path
            d='M3.36 36c0 1.47 1.19 2.67 2.67 2.67 1.47 0 2.67-1.2 2.67-2.67 0-1.47-1.2-2.67-2.67-2.67-1.48 0-2.67 1.2-2.67 2.67zm71.04.35c.2-.2.2-.52 0-.71l-3.18-3.18a.5.5 0 0 0-.71 0c-.2.2-.2.52 0 .71l2.83 2.83-2.83 2.83c-.2.2-.2.52 0 .71.2.2.52.2.71 0l3.18-3.18zM6.03 36v.5h68.01v-1H6.03V36z'
            fill='#254B86'
          />
        </svg>
      </div>

      {/* Right Side - External App Field (Text Input) */}
      <div
        className='external-app-field'
        style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <div
          className='external-app-field-inner'
          style={{
            borderColor: validationError
              ? 'var(--color-border-critical-medium)'
              : undefined,
            borderWidth: validationError ? '2px' : undefined,
          }}>
          <input
            type='text'
            value={row.externalAppField}
            onChange={(e) => handleExternalFieldChange(e.target.value)}
            onBlur={onBlur}
            placeholder='Enter external app field'
            disabled={disabled}
            className='external-app-input'
          />
        </div>
        {validationError && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginTop: '4px',
              color: '#d32f2f',
              fontSize: '0.75rem',
            }}>
            <Icon
              kind='AlertCriticalFilled'
              color='--color-foreground-critical-medium'
            />
            <span>{validationError}</span>
          </div>
        )}
      </div>

      {/* Delete Button */}
      <div className='delete-button-container'>
        {!disabled && canDelete && (
          <button
            type='button'
            className='attribute-mapping-delete'
            onClick={handleDelete}
            aria-label='Delete this mapping'>
            <TrashIconSVG />
          </button>
        )}
      </div>
    </div>
  );
}

export default AttributeRow;
